'use client';

import { flip, offset, shift, size } from '@floating-ui/react';
import type { Editor } from '@tiptap/react';
import { type ComponentType, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

// --- Hooks ---
import { useFloatingElement } from '@/hooks/use-floating-element';

// --- Lib ---
import { getElementOverflowPosition } from '@/lib/tiptap-collab-utils';

// --- UI Primitives ---
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import { Input, InputGroup } from '@/components/tiptap-editor/tiptap-ui-primitive/input';

const MAX_DROPDOWN_HEIGHT = 384;

export interface TriggerDropdownOption {
    label: string;
    icon: ComponentType<{ className?: string }>;
    check?: (editor: Editor) => boolean;
    command: (editor: Editor) => void;
}

export interface TriggerDropdownState {
    isOpen: boolean;
    referenceRect: DOMRect | null;
    open: (editor: Editor) => void;
    close: () => void;
}

export interface TriggerDropdownProps {
    editor: Editor;
    options: TriggerDropdownOption[];
    isOpen: boolean;
    referenceRect: DOMRect | null;
    onClose: () => void;
    ariaLabel: string;
    placeholder: string;
    emptyMessage: string;
    dataSelector?: string;
}

export function getSelectionAnchorRect(editor: Editor): DOMRect | null {
    if (editor.isDestroyed) {
        return null;
    }

    try {
        const { from } = editor.state.selection;
        const coords = editor.view.coordsAtPos(from);
        return new DOMRect(coords.left, coords.bottom, 0, 0);
    } catch {
        return null;
    }
}

function TriggerDropdownItem({
    option,
    isSelected,
    dataSelector,
    onSelect,
}: {
    option: TriggerDropdownOption;
    isSelected: boolean;
    dataSelector: string;
    onSelect: () => void;
}) {
    const itemRef = useRef<HTMLButtonElement>(null);
    const Icon = option.icon;

    useEffect(() => {
        const container = document.querySelector(`[data-selector="${dataSelector}"]`) as HTMLElement | null;
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Disabled since this is TipTap code
        if (!itemRef.current || !isSelected || !container) {
            return;
        }

        const overflow = getElementOverflowPosition(itemRef.current, container);

        if (overflow === 'top' || overflow === 'bottom') {
            itemRef.current.scrollIntoView({ block: 'nearest' });
        }
    }, [isSelected, dataSelector]);

    return (
        <Button
            ref={itemRef}
            type="button"
            data-style="ghost"
            data-active-state={isSelected ? 'on' : 'off'}
            onClick={onSelect}
        >
            <Icon className="tiptap-button-icon" />
            <div className="tiptap-button-text">{option.label}</div>
        </Button>
    );
}

export function useTriggerDropdown(): TriggerDropdownState {
    const [isOpen, setIsOpen] = useState(false);
    const [referenceRect, setReferenceRect] = useState<DOMRect | null>(null);

    const open = useCallback((editor: Editor) => {
        // Defer until after the slash menu finishes its command/cleanup so
        // floating-ui dismiss does not immediately close the follow-up menu.
        queueMicrotask(() => {
            const anchorRect = getSelectionAnchorRect(editor);
            if (!anchorRect) {
                return;
            }

            setReferenceRect(anchorRect);
            setIsOpen(true);
        });
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
    }, []);

    return {
        isOpen,
        referenceRect,
        open,
        close,
    };
}

export function TriggerDropdown({
    editor,
    options,
    isOpen,
    referenceRect,
    onClose,
    ariaLabel,
    placeholder,
    emptyMessage,
    dataSelector = 'tiptap-trigger-dropdown',
}: TriggerDropdownProps) {
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
    if (isOpen !== prevIsOpen) {
        setPrevIsOpen(isOpen);
        if (isOpen) {
            setQuery('');
            setSelectedIndex(0);
        }
    }

    const visibleOptions = options.filter(option => !option.check || option.check(editor));
    const normalizedQuery = query.trim().toLowerCase();
    const filteredOptions = normalizedQuery
        ? visibleOptions.filter(option => option.label.toLowerCase().includes(normalizedQuery))
        : visibleOptions;
    const clampedSelectedIndex = filteredOptions.length === 0 ? 0 : Math.min(selectedIndex, filteredOptions.length - 1);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const frameId = requestAnimationFrame(() => {
            const input = inputRef.current;
            if (!input) {
                return;
            }

            input.focus({ preventScroll: true });
        });

        return () => cancelAnimationFrame(frameId);
    }, [isOpen]);

    const handleClose = useCallback(() => {
        onClose();
        editor.chain().focus().run();
    }, [editor, onClose]);

    const handleOptionSelect = useCallback(
        (index: number) => {
            const option = filteredOptions[index];
            if (!option) {
                return;
            }

            option.command(editor);
            handleClose();
        },
        [editor, filteredOptions, handleClose],
    );

    const handleInputKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                if (!filteredOptions.length) {
                    return;
                }
                setSelectedIndex(previous => (previous + 1) % filteredOptions.length);
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                if (!filteredOptions.length) {
                    return;
                }
                setSelectedIndex(previous => (previous - 1 + filteredOptions.length) % filteredOptions.length);
                return;
            }

            if (event.key === 'Enter') {
                event.preventDefault();
                handleOptionSelect(clampedSelectedIndex);
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                handleClose();
            }
        },
        [clampedSelectedIndex, filteredOptions.length, handleClose, handleOptionSelect],
    );

    const { ref, style, getFloatingProps, isMounted } = useFloatingElement(isOpen, referenceRect, 1000, {
        placement: 'bottom-start',
        middleware: [
            offset(10),
            flip({
                mainAxis: true,
                crossAxis: false,
            }),
            shift(),
            size({
                apply({ availableHeight, elements }) {
                    if (elements.floating) {
                        const maxHeight = Math.min(MAX_DROPDOWN_HEIGHT, availableHeight);
                        elements.floating.style.setProperty('--suggestion-menu-max-height', `${maxHeight}px`);
                    }
                },
            }),
        ],
        onOpenChange: open => {
            if (!open) {
                handleClose();
            }
        },
    });

    if (!isMounted || !isOpen || !referenceRect) {
        return null;
    }

    return (
        <div
            ref={ref}
            style={style}
            {...getFloatingProps()}
            className="tiptap-suggestion-menu"
            data-selector={dataSelector}
            role="dialog"
            aria-label={ariaLabel}
        >
            <Card
                className="tiptap-slash-card"
                style={{
                    minWidth: '15rem',
                    maxWidth: '20rem',
                    maxHeight: 'var(--suggestion-menu-max-height)',
                }}
            >
                <CardBody className="tiptap-slash-card-body">
                    <InputGroup>
                        <Input
                            ref={inputRef}
                            type="text"
                            value={query}
                            placeholder={placeholder}
                            onChange={event => setQuery(event.target.value)}
                            onKeyDown={handleInputKeyDown}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                        />
                    </InputGroup>

                    <div className="mt-2 flex flex-col gap-1">
                        {filteredOptions.length ? (
                            filteredOptions.map((option, index) => (
                                <TriggerDropdownItem
                                    key={option.label}
                                    option={option}
                                    isSelected={index === clampedSelectedIndex}
                                    dataSelector={dataSelector}
                                    onSelect={() => handleOptionSelect(index)}
                                />
                            ))
                        ) : (
                            <div className="px-2 py-1 text-sm text-neutral-500">{emptyMessage}</div>
                        )}
                    </div>
                </CardBody>
            </Card>
        </div>
    );
}
